/** Parse dscsaViolations JSON from API into display strings. */
export function parseDscsaViolations(raw?: string | null): string[] {
    if (!raw || !raw.trim()) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));
        }
        if (typeof parsed === 'object' && parsed !== null) {
            return Object.entries(parsed as Record<string, unknown>).map(
                ([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`
            );
        }
    } catch {
        /* plain text */
    }
    return [raw];
}

/** Parse dscsaViolations from validation preview JSON. */
export function parsePreviewDscsaViolations(previewJson?: string | null): string[] {
    if (!previewJson || !previewJson.trim()) return [];
    try {
        const parsed = JSON.parse(previewJson);
        if (parsed && Array.isArray(parsed.dscsaViolations)) {
            return parsed.dscsaViolations.map((v: unknown) =>
                typeof v === 'string' ? v : JSON.stringify(v)
            );
        }
    } catch {
        /* ignore */
    }
    return [];
}

/** Parse dscsaCompliant from validation preview JSON. */
export function parsePreviewDscsaCompliant(previewJson?: string | null): boolean | null {
    if (!previewJson || !previewJson.trim()) return null;
    try {
        const parsed = JSON.parse(previewJson);
        if (parsed && typeof parsed.dscsaCompliant === 'boolean') {
            return parsed.dscsaCompliant;
        }
    } catch {
        /* ignore */
    }
    return null;
}

/** Parse semanticWarnings from preview JSON or validationPreviewJson. */
export function parseSemanticWarnings(previewJson?: string | null): string[] {
    if (!previewJson || !previewJson.trim()) return [];
    try {
        const parsed = JSON.parse(previewJson);
        if (parsed && Array.isArray(parsed.semanticWarnings)) {
            return parsed.semanticWarnings.map((v: unknown) =>
                typeof v === 'string' ? v : JSON.stringify(v)
            );
        }
    } catch {
        /* ignore */
    }
    return [];
}

export function parsePreviewSchemaWarnings(previewJson?: string | null): string[] {
    if (!previewJson || !previewJson.trim()) return [];
    try {
        const parsed = JSON.parse(previewJson);
        if (parsed && Array.isArray(parsed.schemaWarnings)) {
            return parsed.schemaWarnings.map((v: unknown) =>
                typeof v === 'string' ? v : JSON.stringify(v)
            );
        }
    } catch {
        /* ignore */
    }
    return [];
}

export function parseL2StructuralFallback(previewJson?: string | null): boolean {
    if (!previewJson || !previewJson.trim()) return false;
    try {
        const parsed = JSON.parse(previewJson);
        return parsed?.l2StructuralFallbackUsed === true;
    } catch {
        return false;
    }
}

export function inferFormatSupported(
    formatOverview?: { supported?: boolean } | null,
    detectedSchemaVersion?: string | null,
    previewJson?: string | null
): boolean | null {
    if (formatOverview?.supported === true) return true;
    if (formatOverview?.supported === false) return false;
    if (detectedSchemaVersion) return true;
    if (previewJson?.trim()) {
        try {
            const parsed = JSON.parse(previewJson);
            if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) return true;
        } catch {
            /* ignore */
        }
    }
    return null;
}

/** Parse JSON string array fields (schemaErrors, validationWarnings) from API. */
export function parseJsonStringList(raw?: string | null): string[] {
    if (!raw || !raw.trim()) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));
        }
    } catch {
        /* plain text fallback */
    }
    return raw.includes('\n') ? raw.split('\n').filter(Boolean) : [raw];
}

export function formatTransferDate(value?: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** True only when inbound EPCIS schema and DSCSA both explicitly passed. */
export function isInboundValidationPassing(
    schemaValid?: boolean | null,
    dscsaCompliant?: boolean | null
): boolean {
    return schemaValid === true && dscsaCompliant === true;
}
